import { Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Breadcrumbs,
  Divider,
  Group,
  Space,
} from "@mantine/core";
import {
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import PageHeading from "../../../themes/components/PageHeading";
import PageContainer from "../../../themes/components/PageContainer";
import PayslipIssuesTable from "../../payslips/components/PayslipIssuesTable";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import EmptyStateContainer from "../../../themes/components/EmptyStateContainer";
import DocumentImporterConfiguration from "../../payslips/services/model/DocumentImporterConfiguration";
import { irp5JourneyConfiguration } from "../irp5JourneyConfiguration";
import useUnmatchedDocumentDetailsApiService from "../../payslips/services/api/UnmatchedDocumentDetailsApiService";
import UnmatchedDocument from "../../payslips/services/model/UnmatchedDocument";
import NotFound from "../../../common/pages/NotFound";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";

const IRP5IssuesPage: React.FC = () => {
  const documentImporterConfiguration: DocumentImporterConfiguration =
    irp5JourneyConfiguration;

  const [selection, setSelection] = useState<UnmatchedDocument[]>([]);
  const navigate = useNavigate();

  const { hasPermissions } = React.useContext(AccessPermissionContext);
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);

  const { id } = useParams();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { state }: any = useLocation();

  const { isLoading, isError, data, error } =
    useUnmatchedDocumentDetailsApiService({ id: Number(id) });

  useEffect(() => {
    if (data) {
      trackDocumentsEvent(
        {
          event: `Unmatched_${documentImporterConfiguration.document_name_plural}_Dashboard`,
          description: `User views the unmatched ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
          event_type: "PageView",
          type: documentImporterConfiguration.document_name_plural,
          job: data,
        },
        {
          notification_sent_status: data.should_send_notifications,
        }
      );
    }
  }, [data]);

  if (!hasPermissions) {
    return <NotFound />;
  }

  if (!id) {
    return <Navigate to="/irp5s" />;
  }

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  return (
    <PageContainer>
      <PageHeading
        title={`Unmatched ${documentImporterConfiguration.document_name_plural}`}
      />
      <Space
        style={{
          paddingTop: "32px",
        }}
      />
      <Group position="apart">
        <Breadcrumbs separator=">">
          {[
            {
              title: documentImporterConfiguration.document_name_plural,
              href: "/irp5s",
            },
            { title: "Error", href: "#" },
            {
              title:
                !data || !data.documents.length
                  ? `document-job-${id}`
                  : data.batch_name,
              href: "",
            },
          ].map((item) => (
            <Anchor
              style={{
                color:
                  item.title === `document-job-${id}`
                    ? "#062133"
                    : "rgba(6, 33, 51, 0.5)",
              }}
              href={item.href}
              key={`payslips-path-${Math.random()}`}
            >
              {item.title}
            </Anchor>
          ))}
        </Breadcrumbs>

        <ActionIcon>
          <Search strokeWidth={1.7} size={18} />
        </ActionIcon>
      </Group>
      <Space h="xl" />
      <Divider my="sm" />
      {!data || !data.documents.length ? (
        <EmptyStateContainer
          numberOfSpaces={1}
          title={`No unmatched ${documentImporterConfiguration.document_name_plural_lower_case}`}
          description={`All of the unmatched ${documentImporterConfiguration.document_name_lower_case} issues have been resolved.`}
          button={{
            onClick: () => navigate("/irp5s"),
            title: `Back to ${documentImporterConfiguration.document_name_plural}`,
          }}
        />
      ) : (
        <>
          <Space h="xl" />
          <PayslipIssuesTable
            documentImporterConfiguration={documentImporterConfiguration}
            data={data}
            selection={selection}
            onSelection={setSelection}
            jobStatus={state.jobStatus}
          />
        </>
      )}
    </PageContainer>
  );
};

export default IRP5IssuesPage;
